import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Loader2 } from 'lucide-react';

// Minimal markdown renderer — handles the patterns present in these documents:
// **bold**, # headings, --- horizontal rules, paragraphs.
const renderInline = (text) => {
  const parts = text.split(/(\*\*[^*]+\*\*)/g);
  return parts.map((part, i) =>
    part.startsWith('**') && part.endsWith('**')
      ? <strong key={i}>{part.slice(2, -2)}</strong>
      : part
  );
};

const renderMarkdown = (text) =>
  text.split('\n').map((line, i) => {
    if (/^-{3,}$/.test(line.trim()))
      return <hr key={i} className="border-zinc-700 my-4" />;

    const hMatch = line.match(/^(#{1,4})\s+(.+)/);
    if (hMatch) {
      const cls = hMatch[1].length === 1
        ? 'text-base font-bold mt-6 mb-1 text-white'
        : 'text-sm font-semibold mt-4 mb-0.5 text-zinc-200';
      return <div key={i} className={cls}>{renderInline(hMatch[2])}</div>;
    }

    if (!line.trim()) return <div key={i} className="h-2" />;

    return (
      <p key={i} className="text-sm text-zinc-300 leading-relaxed">
        {renderInline(line)}
      </p>
    );
  });

export const LegalModal = ({ title, docUrl, documentContent, downloadName = 'documento-legal.md', onAccept, onLogout, showBillingCheckbox = false, administrativeCopy = false }) => {
  const [content, setContent] = useState('');
  const [loadingDoc, setLoadingDoc] = useState(true);
  const [checked, setChecked] = useState(false);
  const [billingChecked, setBillingChecked] = useState(false);
  const [accepting, setAccepting] = useState(false);
  const [acceptError, setAcceptError] = useState('');
  const [documentError, setDocumentError] = useState(false);
  const [documentRetry, setDocumentRetry] = useState(0);

  useEffect(() => {
    setLoadingDoc(true);
    setChecked(false);
    setBillingChecked(false);
    setAcceptError('');
    setDocumentError(false);
    if (documentContent !== undefined) {
      setContent(documentContent || '');
      setLoadingDoc(false);
      return undefined;
    }
    fetch(docUrl)
      .then((r) => {
        if (r.ok === false) throw new Error('document request failed');
        return r.text();
      })
      .then((text) => setContent(text))
      .catch(() => {
        setContent('Error al cargar el documento. Reintenta para poder continuar.');
        setDocumentError(true);
      })
      .finally(() => setLoadingDoc(false));
  }, [docUrl, documentContent, documentRetry]);

  const handleAccept = async () => {
    setAccepting(true);
    setAcceptError('');
    try {
      await onAccept();
    } catch (error) {
      setAcceptError(error?.response?.data?.detail || 'No se pudo registrar la aceptación. Puedes reintentarlo.');
    } finally {
      setAccepting(false);
    }
  };

  const canAccept = checked && (!showBillingCheckbox || billingChecked);

  return (
    <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-[2000] px-4 py-6">
      <div className="bg-zinc-900 border border-zinc-700 rounded-2xl w-full max-w-2xl shadow-xl flex flex-col max-h-[90vh]">

        {/* Header */}
        <div className="px-6 pt-6 pb-4 border-b border-zinc-800 flex-shrink-0">
          <h2 className="text-lg font-semibold">{title}</h2>
          <p className="text-xs text-zinc-500 mt-1">
            Lee el documento completo antes de aceptar.
          </p>
        </div>

        {/* Document body */}
        <div className="flex-1 overflow-y-auto px-6 py-4 min-h-0">
          {loadingDoc ? (
            <div className="flex justify-center py-12">
              <Loader2 className="w-6 h-6 animate-spin text-zinc-500" />
            </div>
          ) : (
            <div className="space-y-0.5">
              {renderMarkdown(content)}
            </div>
          )}
          {documentError && (
            <div role="alert" className="text-sm text-red-300 mt-3">
              No se pudo cargar el documento.
              <button type="button" className="underline ml-2" onClick={() => setDocumentRetry((value) => value + 1)}>
                Reintentar
              </button>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 pb-6 pt-4 border-t border-zinc-800 flex-shrink-0 space-y-3">
          <label className="flex items-start gap-3 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={checked}
              onChange={(e) => setChecked(e.target.checked)}
              className="mt-0.5 w-4 h-4 accent-white flex-shrink-0"
              disabled={loadingDoc}
            />
            <span className="text-sm text-zinc-300">
              {administrativeCopy
                ? 'He leído y acepto el presente documento en nombre del establecimiento que represento, y confirmo que tengo facultades para obligarle jurídicamente.'
                : 'He leído y acepto este documento.'}
            </span>
          </label>

          <a
            href={`data:text/markdown;charset=utf-8,${encodeURIComponent(content)}`}
            download={downloadName}
            className="text-sm text-violet-300 hover:text-violet-200 underline"
          >
            Descargar documento
          </a>

          {showBillingCheckbox && (
            <label className="flex items-start gap-3 cursor-pointer select-none">
              <input
                type="checkbox"
                checked={billingChecked}
                onChange={(e) => setBillingChecked(e.target.checked)}
                className="mt-0.5 w-4 h-4 accent-white flex-shrink-0"
                disabled={loadingDoc}
              />
              <span className="text-sm text-zinc-300">
                Autorizo los cargos recurrentes asociados a la suscripción y recargas automáticas.
              </span>
            </label>
          )}

          {acceptError && <p role="alert" className="text-sm text-red-300">{acceptError}</p>}

          <div className="flex justify-end gap-2 pt-1">
            {onLogout && (
              <Button variant="outline" onClick={onLogout} disabled={accepting}>
                Cerrar sesión
              </Button>
            )}
            <Button onClick={handleAccept} disabled={!canAccept || accepting || loadingDoc || documentError}>
              {accepting
                ? <Loader2 className="w-4 h-4 animate-spin" />
                : 'Aceptar'}
            </Button>
          </div>
        </div>

      </div>
    </div>
  );
};
